import { useState, useRef, useEffect } from "react"
import { Download, Filter, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus, MapPin, Package, Store } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/chart/select"
import { Card, CardContent } from "../ui/chart/card"
import { bar_chart } from "../../lib/apis"
import axios from "axios"
import { useLocation, useNavigate } from "react-router-dom"
import Loader from "../loader"
import { useTranslation } from "react-i18next"
import { FaArrowLeft } from "react-icons/fa"
import Breadcrumb from "../Breadcrumb"
import { motion, AnimatePresence } from "framer-motion"
import { scrollToTop } from "../../lib/utils"

type Product = {
  product_id: number;
  product_name: string;
};

type Arrondissement = {
  arrondissement_id: number;
  arrondissement_name: string;
};

type Uml = {
  uml_name: string;
};

type Market = {
  latest_price: number;
  market_id: number;
  market_name: string;
};

type BarChartData = {
  products: {
    [key: string]: Product[];
  };
  arrondissements: {
    [key: string]: Arrondissement[];
  };
  umls: {
    [key: string]: Uml[];
  };
  markets: {
    [key: string]: Market[];
  };
  selected_uml: string;
  selected_arrondissement: Arrondissement
};

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price) + " "
}

export default function PriceAnalysis() {
  const { t, i18n } = useTranslation()
  const { state } = useLocation()
  const parsedProduct = state?.product
  const [product, setProduct] = useState(i18n.language === 'fr' ? parsedProduct?.product_name : parsedProduct?.product_name_en)
  const [location, setLocation] = useState(i18n.language === 'fr' ? parsedProduct?.arrondissement_name : parsedProduct?.arrondissement_name_en)
  const [productId, setProductId] = useState(parsedProduct?.product_id);
  const [arrondissementId, setArrondissementId] = useState(parsedProduct?.arrondissement_id);
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const token = localStorage.getItem("token")
  const [activeMarket, setActiveMarket] = useState(i18n.language === 'fr' ? parsedProduct?.market_name : parsedProduct?.market_name_en)
  const [showTooltip, setShowTooltip] = useState(true)
  const svgRef = useRef<HTMLDivElement>(null)
  const [barChartbyMarkets, setBarChartbyMarkets] = useState<BarChartData | null>(null);
  const [packageType, setPackageType] = useState(i18n.language === 'fr' ? parsedProduct?.collect_type : parsedProduct?.collect_type_en)
  const [selectedLocality, setSelectedLocality] = useState<Arrondissement | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    scrollToTop()
  }, [])

  const fetchUpdatedChart = async (
    updatedProductId: number,
    updatedArrondissementId?: number,
    updatedPackageType?: string
  ) => {
    setLoading(true);
    try {
      let url = `${bar_chart}?product_id=${updatedProductId}`;
      if (updatedArrondissementId) {
        url += `&arrondissement_id=${updatedArrondissementId}`
      }
      if (updatedPackageType) {
        url += `&collect_type=${updatedPackageType}`
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (i18n.language === "fr") {
        setBarChartbyMarkets(response?.data?.data_fr);
        setPackageType(response?.data?.data_fr?.selected_uml)
        setProduct(response?.data?.data_fr?.selected_product?.product_name)
        setSelectedLocality(response?.data?.data_fr?.selected_arrondissement)
        setSelectedProduct(response?.data?.data_fr?.selected_product)
      } else {
        setBarChartbyMarkets(response?.data.data_en);
        setPackageType(response?.data?.data_en?.selected_uml)
        setProduct(response?.data?.data_en?.selected_product?.product_name)
        setSelectedLocality(response?.data?.data_en?.selected_arrondissement)
        setSelectedProduct(response?.data?.data_en?.selected_product)
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du graphique:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) fetchUpdatedChart(productId)
  }, [i18n.language, productId]);


  const allProducts = Object.values(barChartbyMarkets?.products || {}).flat();
  const allArrondissements = Object.values(barChartbyMarkets?.arrondissements || {}).flat();
  const allUmls = Object.values(barChartbyMarkets?.umls || {}).flat();
  const allMarkets = Object.values(barChartbyMarkets?.markets || {}).flat();

  const avgPrice = Math.round(allMarkets.reduce((sum, m) => sum + Number(m?.latest_price), 0) / allMarkets.length)
  const maxPrice = Math.max(...allMarkets.map((m) => Number(m?.latest_price)))
  const minPrice = Math.min(...allMarkets.map((m) => Number(m?.latest_price)))
  const activeData = allMarkets.find((m) => m?.market_name === activeMarket) || allMarkets[0]

  const getProductIdByName = (name: string) => {
    return allProducts.find(p => p.product_name === name)?.product_id;
  };

  const getArrondissementIdByName = (name: string) => {
    return allArrondissements.find(a => a.arrondissement_name === name)?.arrondissement_id;
  };

  const handleProductChange = async (newProductName: string) => {
    setProduct(newProductName);
    const id = getProductIdByName(newProductName);
    if (id !== undefined) {
      setProductId(id);
      fetchUpdatedChart(id);
      setPackageType(barChartbyMarkets?.selected_uml);
    }
  };

  const handleArrondissementChange = async (newArrondissement: string) => {
    setLocation(newArrondissement);
    const id = getArrondissementIdByName(newArrondissement);
    if (id !== undefined) {
      setArrondissementId(id);
      fetchUpdatedChart(productId, id);
    }
  };

  const handlePackageTypeChange = async (newPackageType: string) => {
    setPackageType(newPackageType);
    fetchUpdatedChart(productId, arrondissementId, newPackageType);
  };

  const handleBarHover = (market: string): void => {
    setActiveMarket(market)
    setShowTooltip(true)
  }

  const highest_market = allMarkets.find((m) => Number(m.latest_price) === maxPrice)?.market_name
  const lowest_market = allMarkets.find((m) => Number(m.latest_price) === minPrice)?.market_name

  function generateYAxisLabels(max: number, steps: number = 5): number[] {
    const interval = Math.ceil(max / steps);
    const labels = [];
    labels.push(max + interval)
    for (let i = max; i >= 0; i -= interval) {
      labels.push(i);
    }
    return labels;
  }

  const yLabels = generateYAxisLabels(maxPrice);
  const chartHeight = 300;
  const maxPriceValue = Math.max(...allMarkets.map(m => Number(m.latest_price)));

  const getBarHeight = (price: number) => {
    return (price / maxPriceValue) * chartHeight;
  };

  const getYPosition = (price: number) => {
    return getBarHeight(price);
  };

  const handleBarClick = (marketData: Market) => {
    const updatedProduct = {
      ...parsedProduct,
      market_id: marketData.market_id,
      market_name: marketData.market_name,
      market_name_en: marketData.market_name,
      latest_price: marketData.latest_price,
      product_id: selectedProduct?.product_id,
      product_name: selectedProduct?.product_name,
      product_name_en: selectedProduct?.product_name,
      collect_type: packageType,
      collect_type_en: packageType,
      arrondissement_id: selectedLocality?.arrondissement_id,
      arrondissement_name: selectedLocality?.arrondissement_name,
      arrondissement_name_en: selectedLocality?.arrondissement_name,
    };
    navigate('/analyse', {
      state: {
        product: updatedProduct,
      }
    });
  };

  // Statistiques
  const priceDiff = activeData?.latest_price - avgPrice;
  const priceDiffPercent = avgPrice !== 0 ? Math.round((priceDiff / avgPrice) * 100) : 0;
  const isPriceUp = priceDiff > 0;
  const isPriceDown = priceDiff < 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">

              <Breadcrumb />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Titre */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            {t('product_selected')}{" "}
            <span className="text-green-600">{product}</span>
          </h1>
          <p className="text-gray-500 mt-1 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            {location} • {packageType}
          </p>
        </div>

        {/* Filtres */}
        <div className="mb-6">
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200"
          >
            <Filter className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">{t('filtres') || 'Filtres'}</span>
            {isFilterOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          <AnimatePresence>
            {isFilterOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-4 bg-white rounded-2xl shadow-lg p-6 border border-gray-100 overflow-hidden"
              >
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1.5">
                      {t('produit') || 'Produit'}
                    </label>
                    <Select value={product} onValueChange={handleProductChange}>
                      <SelectTrigger className="w-full bg-gray-50 border-gray-200 rounded-xl">
                        <SelectValue placeholder={product} />
                      </SelectTrigger>
                      <SelectContent>
                        {allProducts.map((item: any, index: any) => {
                          const productName = item?.product_name;
                          return (
                            <SelectItem key={`${productName}-${index}`} value={productName}>
                              {productName}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1.5">
                      {t('localite') || 'Localité'}
                    </label>
                    <Select value={location} onValueChange={handleArrondissementChange}>
                      <SelectTrigger className="w-full bg-gray-50 border-gray-200 rounded-xl">
                        <SelectValue placeholder={location} />
                      </SelectTrigger>
                      <SelectContent>
                        {allArrondissements.map((item: any) => {
                          const ArrondissemntName = item?.arrondissement_name;
                          return (
                            <SelectItem key={ArrondissemntName} value={ArrondissemntName}>
                              {ArrondissemntName}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-1.5">
                      {t('conditionnement') || 'Conditionnement'}
                    </label>
                    <Select value={packageType} onValueChange={handlePackageTypeChange}>
                      <SelectTrigger className="w-full bg-gray-50 border-gray-200 rounded-xl">
                        <SelectValue placeholder={packageType} />
                      </SelectTrigger>
                      <SelectContent>
                        {allUmls?.map((item: any) => {
                          const umlName = item;
                          if (!umlName || umlName === '') return null;
                          return (
                            <SelectItem key={umlName} value={umlName}>
                              {umlName}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-end">
                    <button className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all duration-300 shadow-md hover:shadow-lg">
                      <Download className="w-4 h-4" />
                      <span className="text-sm font-medium">{t('export') || 'Exporter'}</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Graphique */}
        <Card className="bg-white shadow-lg border-0 rounded-2xl overflow-hidden mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-sm font-medium text-gray-500">
                  {t('evolution_prix') || 'Évolution des prix'}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {allMarkets.length} {t('marches') || 'marchés'}
                </p>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                  <span className="text-gray-600">{t('marche_actif') || 'Marché actif'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                  <span className="text-gray-600">{t('autres_marches') || 'Autres marchés'}</span>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="py-12">
                <Loader />
              </div>
            ) : allMarkets.length > 0 ? (
              <div className="relative">
                {/* Conteneur du graphique avec dimensions fixes */}
                <div className="relative w-full" style={{ height: '400px' }}>
                  {/* Marge pour les labels Y */}
                  <div className="absolute left-0 top-0 bottom-0 w-16">
                    {/* Labels Y */}
                    <div className="relative w-full h-full">
                      {yLabels.map((label, idx) => {
                        const position = (1 - (label / maxPriceValue)) * 100;
                        return (
                          <div
                            key={idx}
                            className="absolute right-2 text-xs text-gray-500"
                            style={{ top: `${Math.min(Math.max(position, 0), 100)}%`, transform: 'translateY(-50%)' }}
                          >
                            {label}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Zone du graphique */}
                  <div className="absolute left-16 right-0 top-0 bottom-0">
                    {/* Lignes de la grille */}
                    <div className="relative w-full h-full">
                      {yLabels.map((label, idx) => {
                        const position = (1 - (label / maxPriceValue)) * 100;
                        return (
                          <div
                            key={idx}
                            className="absolute left-0 right-0 border-t border-gray-200 border-dashed"
                            style={{ top: `${Math.min(Math.max(position, 0), 100)}%` }}
                          />
                        );
                      })}

                      {/* Barres */}
                      <div className="relative w-full h-full flex items-end justify-around px-2">
                        {allMarkets.map((data, index) => {
                          const barHeight = (Number(data.latest_price) / maxPriceValue) * 85; // 85% de la hauteur pour laisser de la place
                          const isActive = data.market_name === activeMarket;

                          return (
                            <div
                              key={index}
                              className="flex flex-col items-center cursor-pointer group"
                              style={{ height: '100%', justifyContent: 'flex-end' }}
                              onMouseEnter={() => handleBarHover(data.market_name)}
                              onClick={() => handleBarClick(data)}
                            >
                              {/* Tooltip */}
                              {isActive && showTooltip && (
                                <div className="mb-1 px-3 py-1.5 bg-gray-800 rounded-lg text-white text-xs font-medium shadow-lg whitespace-nowrap">
                                  {formatPrice(data.latest_price)} <span className="text-[8px] font-bold text-[#888] whitespace-nowrap">FCFA</span>
                                  <span className="text-gray-400 text-[10px] ml-1">/{packageType}</span>
                                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
                                </div>
                              )}

                              {/* Barre */}
                              <div
                                className={`w-12 md:w-16 rounded-t-lg transition-all duration-300 ${isActive ? 'bg-green-600' : 'bg-gray-400'
                                  } ${isActive ? 'opacity-100' : 'opacity-60 group-hover:opacity-80'}`}
                                style={{
                                  height: `${barHeight}%`,
                                  minHeight: '4px'
                                }}
                              />

                              {/* Label du marché */}
                              <div
                                className={`mt-2 text-xs text-center transition-all duration-300 ${isActive ? 'text-green-700 font-semibold' : 'text-gray-500'
                                  }`}
                                style={{
                                  maxWidth: '80px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {data.market_name}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">{t('emptyMarket')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Analyse - Cartes uniformes */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            {t('analyse')}
          </h2>

          {loading ? (
            <Loader />
          ) : avgPrice && activeData && maxPrice && minPrice ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 font-dm lg:grid-cols-4">
              {/* Carte Prix Moyen */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                className="group relative overflow-hidden rounded-xl border border-gray-100 bg-primary/10 p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg"
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-third to-secondary" />
                <div className="flex items-start justify-between gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                    <TrendingUp className="h-[18px] w-[18px] text-primary" />
                  </div>
                  <span className="rounded-full bg-primary/10 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-primary">
                    {t('sur')} {allMarkets.length}
                  </span>
                </div>
                <div className="mt-5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('averageprice')}</p>
                  <p className="mt-1 text-2xl font-black tracking-tight text-gray-900">{formatPrice(avgPrice)}<span className="text-xs font-bold text-[#888] whitespace-nowrap">FCFA</span></p>
                  <p className="mt-1 text-[10px] text-gray-400">{t('marches')}</p>
                </div>
              </motion.div>

              {/* Carte Marché Actif */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="group relative overflow-hidden rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#93C01F]/50 hover:shadow-lg"
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[#93C01F]" />
                <div className="flex items-start justify-between gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#93C01F]/15">
                    <Store className="h-[18px] w-[18px] text-primary" />
                  </div>
                  <span className="h-2 w-2 rounded-full bg-[#93C01F] shadow-[0_0_0_4px_rgba(147,192,31,0.14)]" />
                </div>
                <div className="mt-5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('marche_actif')}</p>
                  <p className="mt-1 truncate text-sm font-bold text-gray-800">{activeMarket}</p>
                  <p className="mt-1 text-xl font-black tracking-tight text-primary">{formatPrice(activeData?.latest_price)}<span className="text-xs font-bold text-[#888] whitespace-nowrap">FCFA</span></p>
                  <div className={`mt-1 flex items-center gap-1 text-[10px] ${isPriceUp ? 'text-secondary' : isPriceDown ? 'text-primary' : 'text-gray-400'}`}>
                    {isPriceUp ? <TrendingUp className="h-3 w-3" /> : isPriceDown ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                    <span>{isPriceUp ? '+' : ''}{priceDiffPercent}% {t('vs_moyenne')}</span>
                  </div>
                </div>
              </motion.div>

              {/* Carte Prix Max */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="group relative overflow-hidden rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-secondary/30 hover:shadow-lg"
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-secondary" />
                <div className="flex items-start justify-between gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary/10">
                    <TrendingUp className="h-[18px] w-[18px] text-secondary" />
                  </div>
                  <span className="rounded-full bg-secondary/10 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-secondary">Haut</span>
                </div>
                <div className="mt-5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('prix_max')}</p>
                  <p className="mt-1 truncate text-sm font-bold text-gray-800">{highest_market}</p>
                  <p className="mt-1 text-xl font-black tracking-tight text-secondary">{formatPrice(maxPrice)}<span className="text-xs font-bold text-[#888] whitespace-nowrap">FCFA</span></p>
                  <p className="mt-1 text-[10px] text-gray-400">+{Math.round(((maxPrice - avgPrice) / avgPrice) * 100)}% {t('vs_moyenne')}</p>
                </div>
              </motion.div>

              {/* Carte Prix Min */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                className="group relative overflow-hidden rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg"
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-primary" />
                <div className="flex items-start justify-between gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                    <TrendingDown className="h-[18px] w-[18px] text-primary" />
                  </div>
                  <span className="rounded-full bg-primary/10 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-primary">Bas</span>
                </div>
                <div className="mt-5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{t('prix_min')}</p>
                  <p className="mt-1 truncate text-sm font-bold text-gray-800">{lowest_market}</p>
                  <p className="mt-1 text-xl font-black tracking-tight text-primary">{formatPrice(minPrice)}<span className="text-xs font-bold text-[#888] whitespace-nowrap">FCFA</span></p>
                  <p className="mt-1 text-[10px] text-gray-400">-{Math.round(((avgPrice - minPrice) / avgPrice) * 100)}% {t('vs_moyenne')}</p>
                </div>
              </motion.div>
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl shadow-md">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">{t('noanalyse')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}